import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Fingerprint,
  Server,
  Clock,
  Terminal,
  Globe,
  ChevronDown,
  ChevronUp,
  Download,
  Database,
  Mail,
  Shield,
  Wifi,
  Code,
  Cloud,
  Network,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import {
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts";

// Cloud provider detection from server headers or banners
const CLOUD_PATTERNS: { pattern: RegExp; provider: string; icon: string }[] = [
  { pattern: /cloudfront/i, provider: "AWS CloudFront", icon: "/icons/aws-cloudfront.svg" },
  { pattern: /amazons3|\.s3\./i, provider: "AWS S3", icon: "/icons/aws-s3.svg" },
  { pattern: /awselb|elb\./i, provider: "AWS ELB", icon: "/icons/aws-elb.svg" },
  { pattern: /ec2.*amazonaws/i, provider: "AWS EC2", icon: "/icons/aws-ec2.svg" },
  { pattern: /cloudflare/i, provider: "Cloudflare", icon: "/icons/cloudflare.svg" },
  { pattern: /akamai/i, provider: "Akamai", icon: "/icons/akamai.svg" },
  { pattern: /fastly/i, provider: "Fastly", icon: "/icons/fastly.svg" },
  { pattern: /azure/i, provider: "Azure", icon: "/icons/azure.svg" },
  { pattern: /google|gcp|gws/i, provider: "Google Cloud", icon: "/icons/gcp.svg" },
  { pattern: /vercel/i, provider: "Vercel", icon: "/icons/vercel.svg" },
  { pattern: /netlify/i, provider: "Netlify", icon: "/icons/netlify.svg" },
];

function detectCloudProvider(probes: ProbeResult[]): { provider: string; icon: string } | null {
  for (const probe of probes) {
    // Check HTTP server header
    if (probe.http_info?.server) {
      for (const { pattern, provider, icon } of CLOUD_PATTERNS) {
        if (pattern.test(probe.http_info.server)) {
          return { provider, icon };
        }
      }
    }
    // Check banner
    if (probe.raw_banner) {
      for (const { pattern, provider, icon } of CLOUD_PATTERNS) {
        if (pattern.test(probe.raw_banner)) {
          return { provider, icon };
        }
      }
    }
    // Check service product
    if (probe.service_product) {
      for (const { pattern, provider, icon } of CLOUD_PATTERNS) {
        if (pattern.test(probe.service_product)) {
          return { provider, icon };
        }
      }
    }
  }
  return null;
}

// Types based on backend ProbeResult
type ProbeMethod = "banner" | "protocol_handshake" | "http_probe";

interface HttpProbeInfo {
  status_code: number;
  status_text: string;
  server?: string;
  x_powered_by?: string;
  content_type?: string;
  content_length?: number;
  title?: string;
  meta_generator?: string;
  technologies: string[];
  redirect_url?: string;
  response_headers: Record<string, string>;
}

interface ProbeResult {
  port: number;
  protocol: string;
  probe_method: ProbeMethod;
  raw_banner?: string;
  banner_hex?: string;
  service_name?: string;
  service_product?: string;
  service_version?: string;
  service_extra_info?: string;
  os_hint?: string;
  protocol_info?: Record<string, unknown>;
  http_info?: HttpProbeInfo;
  confidence: number;
  probe_time_ms: number;
  error?: string;
}

interface ProbeStats {
  total_ports: number;
  identified_services: number;
  versioned_services: number;
  unique_services: string[];
  unique_products: string[];
}

interface ServiceProbeResultData {
  target: string;
  source_subdomain?: string; // deprecated, use source_subdomains
  source_subdomains?: string[]; // all subdomains that resolve to this IP
  probes: ProbeResult[];
  probe_duration_ms: number;
  successful_probes: number;
  failed_probes: number;
  stats: ProbeStats;
}

interface AggregateProbeStats {
  total_ports_probed: number;
  total_identified: number;
  all_unique_services: string[];
  all_unique_products: string[];
  total_duration_ms: number;
}

export interface ServiceProbeModuleData {
  score?: number;
  source_domain?: string;
  probed_targets?: number;
  total_requested?: number;
  results?: ServiceProbeResultData[];
  aggregate_stats?: AggregateProbeStats;
  standalone_mode?: boolean;
  // Legacy single-target format
  target?: string;
  probes?: ProbeResult[];
  stats?: ProbeStats;
}

export interface ServiceProbeModuleViewProps {
  data: ServiceProbeModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  hideHeader?: boolean;
}

// Probe method labels
const METHOD_LABELS: Record<ProbeMethod, string> = {
  banner: "Banner Grab",
  protocol_handshake: "Protocol Handshake",
  http_probe: "HTTP Probe",
};

// Service category icons
const SERVICE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  ssh: Terminal,
  http: Globe,
  https: Shield,
  ftp: Database,
  mysql: Database,
  postgresql: Database,
  redis: Database,
  mongodb: Database,
  smtp: Mail,
  imap: Mail,
  pop3: Mail,
};

// Service category colors - cyberpunk/security dashboard palette
type ServiceCategory = "web" | "remote" | "database" | "email" | "other";

const SERVICE_CATEGORIES: Record<string, ServiceCategory> = {
  http: "web",
  https: "web",
  ssh: "remote",
  ftp: "remote",
  sftp: "remote",
  telnet: "remote",
  rdp: "remote",
  vnc: "remote",
  mysql: "database",
  postgresql: "database",
  postgres: "database",
  redis: "database",
  mongodb: "database",
  mssql: "database",
  smtp: "email",
  smtps: "email",
  imap: "email",
  imaps: "email",
  pop3: "email",
  pop3s: "email",
};

const CATEGORY_STYLES: Record<ServiceCategory, { bg: string; border: string; text: string; glow: string }> = {
  web: {
    bg: "bg-cyan-500/15",
    border: "border-cyan-500/40",
    text: "text-cyan-400",
    glow: "shadow-[0_0_10px_rgba(0,245,212,0.15)]",
  },
  remote: {
    bg: "bg-purple-500/15",
    border: "border-purple-500/40",
    text: "text-purple-400",
    glow: "shadow-[0_0_10px_rgba(168,85,247,0.15)]",
  },
  database: {
    bg: "bg-amber-500/15",
    border: "border-amber-500/40",
    text: "text-amber-400",
    glow: "shadow-[0_0_10px_rgba(245,158,11,0.15)]",
  },
  email: {
    bg: "bg-rose-500/15",
    border: "border-rose-500/40",
    text: "text-rose-400",
    glow: "shadow-[0_0_10px_rgba(244,63,94,0.15)]",
  },
  other: {
    bg: "bg-slate-500/15",
    border: "border-slate-500/40",
    text: "text-slate-300",
    glow: "",
  },
};

function getServiceCategory(service: string): ServiceCategory {
  return SERVICE_CATEGORIES[service.toLowerCase()] || "other";
}

// Product type colors based on common software categories
const PRODUCT_PATTERNS: { pattern: RegExp; style: { bg: string; border: string; text: string } }[] = [
  // Web servers
  { pattern: /nginx|apache|iis|caddy|lighttpd/i, style: { bg: "bg-emerald-500/15", border: "border-emerald-500/40", text: "text-emerald-400" } },
  // CDN/Cloud
  { pattern: /cloudfront|cloudflare|akamai|fastly|azure|vercel|netlify/i, style: { bg: "bg-sky-500/15", border: "border-sky-500/40", text: "text-sky-400" } },
  // AWS
  { pattern: /amazon|aws|s3/i, style: { bg: "bg-orange-500/15", border: "border-orange-500/40", text: "text-orange-400" } },
  // Databases
  { pattern: /mysql|postgres|redis|mongo|mariadb|oracle/i, style: { bg: "bg-amber-500/15", border: "border-amber-500/40", text: "text-amber-400" } },
  // Security/SSH
  { pattern: /openssh|dropbear|putty/i, style: { bg: "bg-purple-500/15", border: "border-purple-500/40", text: "text-purple-400" } },
  // Mail
  { pattern: /postfix|sendmail|exim|dovecot|exchange/i, style: { bg: "bg-rose-500/15", border: "border-rose-500/40", text: "text-rose-400" } },
  // PHP/CMS
  { pattern: /php|wordpress|drupal|joomla/i, style: { bg: "bg-indigo-500/15", border: "border-indigo-500/40", text: "text-indigo-400" } },
];

function getProductStyle(product: string): { bg: string; border: string; text: string } {
  for (const { pattern, style } of PRODUCT_PATTERNS) {
    if (pattern.test(product)) {
      return style;
    }
  }
  return { bg: "bg-slate-500/10", border: "border-slate-500/30", text: "text-slate-300" };
}

// Confidence color
function getConfidenceColor(confidence: number): string {
  if (confidence >= 90) return "text-emerald-400";
  if (confidence >= 70) return "text-amber-400";
  return "text-slate-400";
}

// Service distribution colors - cyberpunk neon palette
const SERVICE_COLORS = [
  { main: "#00f5d4", glow: "rgba(0, 245, 212, 0.4)" },   // Cyan/Teal
  { main: "#7b61ff", glow: "rgba(123, 97, 255, 0.4)" },  // Purple
  { main: "#00bbf9", glow: "rgba(0, 187, 249, 0.4)" },   // Electric Blue
  { main: "#f72585", glow: "rgba(247, 37, 133, 0.4)" },  // Magenta
  { main: "#fee440", glow: "rgba(254, 228, 64, 0.4)" },  // Yellow
  { main: "#9b5de5", glow: "rgba(155, 93, 229, 0.4)" },  // Violet
  { main: "#00f5a0", glow: "rgba(0, 245, 160, 0.4)" },   // Mint
  { main: "#ff6b6b", glow: "rgba(255, 107, 107, 0.4)" }, // Coral
];

// Protocol acronyms that should be uppercase
const ACRONYM_PROTOCOLS = new Set([
  "ssh", "http", "https", "ftp", "sftp", "ftps", "smtp", "smtps",
  "imap", "imaps", "pop3", "pop3s", "dns", "dhcp", "tcp", "udp",
  "tls", "ssl", "rdp", "vnc", "ntp", "snmp", "ldap", "ldaps",
  "sip", "rtsp", "rtmp", "ws", "wss", "amqp", "mqtt", "grpc",
  "mysql", "mssql", "pgsql", "postgresql", "mongodb", "redis",
  "nfs", "smb", "cifs", "ipmi", "iscsi", "bgp", "ospf", "rip",
]);

// Format service/protocol name - uppercase for acronyms
function formatServiceName(name: string): string {
  if (!name) return name;
  const lower = name.toLowerCase();
  if (ACRONYM_PROTOCOLS.has(lower)) {
    return name.toUpperCase();
  }
  // Capitalize first letter for non-acronyms
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

// Format duration
function formatDuration(ms?: number): string {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// Banner Display Component
function BannerDisplay({ banner, hex }: { banner?: string; hex?: string }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const content = banner || (hex ? `[Binary: ${hex.slice(0, 64)}...]` : null);

  if (!content) return null;

  const isLong = content.length > 80;
  const displayContent = isLong && !isExpanded ? content.slice(0, 80) + "..." : content;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-1">
        <Terminal className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Banner</span>
      </div>
      <pre className="text-xs font-mono bg-muted/50 p-2 rounded border border-border overflow-x-auto whitespace-pre-wrap break-all">
        {displayContent}
      </pre>
      {isLong && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 h-6 text-xs"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show more
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// HTTP Info Display
function HttpInfoDisplay({ info }: { info: HttpProbeInfo }) {
  if (!info) return null;

  const technologies = info.technologies || [];

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          className={cn(
            "text-xs",
            info.status_code >= 200 && info.status_code < 300
              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
              : info.status_code >= 300 && info.status_code < 400
              ? "bg-blue-500/15 text-blue-400 border-blue-500/40"
              : info.status_code >= 400 && info.status_code < 500
              ? "bg-amber-500/15 text-amber-400 border-amber-500/40"
              : "bg-red-500/15 text-red-400 border-red-500/40"
          )}
        >
          {info.status_code} {info.status_text}
        </Badge>
        {info.server && (
          <span className="text-xs text-muted-foreground">
            Server: <span className="text-foreground">{info.server}</span>
          </span>
        )}
      </div>
      {info.title && (
        <div className="text-xs">
          <span className="text-muted-foreground">Title: </span>
          <span className="text-foreground">{info.title}</span>
        </div>
      )}
      {technologies.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {technologies.map((tech) => (
            <Badge key={tech} variant="secondary" className="text-xs">
              <Code className="h-3 w-3 mr-1" />
              {tech}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// Service Distribution - Orbital Radar Visualization
function ServiceDistributionChart({ probes }: { probes: ProbeResult[] }) {
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  const serviceData = React.useMemo(() => {
    if (!probes || !Array.isArray(probes)) return [];
    const counts: Record<string, number> = {};
    probes.forEach((p) => {
      if (p && p.service_name) {
        counts[p.service_name] = (counts[p.service_name] || 0) + 1;
      }
    });
    const sorted = Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);

    const maxValue = Math.max(...sorted.map((d) => d.value));
    return sorted.map((item, idx) => ({
      ...item,
      fill: SERVICE_COLORS[idx % SERVICE_COLORS.length].main,
      glow: SERVICE_COLORS[idx % SERVICE_COLORS.length].glow,
      percent: Math.round((item.value / maxValue) * 100),
    }));
  }, [probes]);

  const totalServices = serviceData.reduce((acc, d) => acc + d.value, 0);

  if (!serviceData || serviceData.length === 0) return null;

  return (
    <div className="relative">
      {/* Background grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: "20px 20px",
        }}
      />

      <div className="flex flex-col lg:flex-row items-center gap-8 p-4">
        {/* Radial Chart */}
        <div className="relative w-[280px] h-[280px] flex-shrink-0">
          {/* Orbital rings background */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 280 280"
          >
            <defs>
              <radialGradient id="orbitalGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="rgba(0, 245, 212, 0.15)" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
            </defs>
            <circle cx="140" cy="140" r="130" fill="url(#orbitalGlow)" />
            {[110, 90, 70, 50].map((r, i) => (
              <circle
                key={r}
                cx="140"
                cy="140"
                r={r}
                fill="none"
                stroke="rgba(255,255,255,0.05)"
                strokeWidth="1"
                strokeDasharray={i % 2 === 0 ? "4 4" : "none"}
              />
            ))}
          </svg>

          {/* Central core */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative">
              {/* Pulsing glow */}
              <div
                className="absolute -inset-4 rounded-full animate-pulse"
                style={{
                  background: "radial-gradient(circle, rgba(0,245,212,0.2) 0%, transparent 70%)",
                }}
              />
              {/* Core circle */}
              <div
                className="relative w-20 h-20 rounded-full flex flex-col items-center justify-center"
                style={{
                  background: "linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.9) 100%)",
                  border: "1px solid rgba(0,245,212,0.3)",
                  boxShadow: "0 0 30px rgba(0,245,212,0.2), inset 0 0 20px rgba(0,245,212,0.1)",
                }}
              >
                <span
                  className="text-2xl font-bold font-mono"
                  style={{ color: "#00f5d4", textShadow: "0 0 10px rgba(0,245,212,0.5)" }}
                >
                  {totalServices}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">
                  Total
                </span>
              </div>
            </div>
          </div>

          {/* Recharts Radial Bar */}
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="40%"
              outerRadius="90%"
              data={serviceData}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis
                type="number"
                domain={[0, 100]}
                angleAxisId={0}
                tick={false}
              />
              <RadialBar
                background={{ fill: "rgba(255,255,255,0.03)" }}
                dataKey="percent"
                cornerRadius={4}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>

        {/* Legend with stats */}
        <div className="flex-1 min-w-0">
          <div className="grid gap-2">
            {serviceData.map((service, idx) => {
              const isHovered = hoveredIndex === idx;
              const IconComponent = SERVICE_ICONS[service.name] || Wifi;

              return (
                <div
                  key={service.name}
                  className={cn(
                    "group relative flex items-center gap-3 p-3 rounded-lg transition-all duration-200 cursor-default",
                    "border border-transparent",
                    isHovered
                      ? "bg-white/[0.08] border-white/10"
                      : "hover:bg-white/[0.04]"
                  )}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    boxShadow: isHovered ? `0 0 20px ${service.glow}` : "none",
                  }}
                >
                  {/* Service icon with glow */}
                  <div
                    className="relative flex h-9 w-9 items-center justify-center rounded-lg transition-transform duration-200"
                    style={{
                      background: `linear-gradient(135deg, ${service.fill}20 0%, ${service.fill}10 100%)`,
                      border: `1px solid ${service.fill}40`,
                      transform: isHovered ? "scale(1.1)" : "scale(1)",
                      boxShadow: isHovered ? `0 0 15px ${service.glow}` : "none",
                    }}
                  >
                    <div style={{ color: service.fill }}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                  </div>

                  {/* Service name and bar */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-slate-200 truncate">
                        {formatServiceName(service.name)}
                      </span>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-mono font-bold"
                          style={{ color: service.fill }}
                        >
                          {service.value}
                        </span>
                        <span className="text-xs text-slate-500">
                          ({Math.round((service.value / totalServices) * 100)}%)
                        </span>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="relative h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
                        style={{
                          width: `${service.percent}%`,
                          background: `linear-gradient(90deg, ${service.fill} 0%, ${service.fill}99 100%)`,
                          boxShadow: isHovered ? `0 0 10px ${service.glow}` : "none",
                        }}
                      />
                      {/* Animated shine effect */}
                      <div
                        className="absolute inset-y-0 left-0 rounded-full opacity-50"
                        style={{
                          width: `${service.percent}%`,
                          background: `linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)`,
                          animation: isHovered ? "shimmer 1.5s infinite" : "none",
                        }}
                      />
                    </div>
                  </div>

                  {/* Hover indicator line */}
                  <div
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-8 rounded-full transition-opacity duration-200"
                    style={{
                      background: service.fill,
                      opacity: isHovered ? 1 : 0,
                      boxShadow: `0 0 8px ${service.glow}`,
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Shimmer animation keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
}

// Export probes as CSV
function exportProbesCsv(probes: ProbeResult[], target?: string) {
  const headers = ["Port", "Protocol", "Service", "Product", "Version", "Method", "Confidence", "Banner", "Response Time (ms)"];
  const csvContent = [
    headers.join(","),
    ...probes.map((p) => [
      p.port,
      p.protocol.toUpperCase(),
      `"${p.service_name || ""}"`,
      `"${p.service_product || ""}"`,
      `"${p.service_version || ""}"`,
      `"${METHOD_LABELS[p.probe_method] || p.probe_method}"`,
      p.confidence,
      `"${(p.raw_banner || "").replace(/"/g, '""').slice(0, 200)}"`,
      p.probe_time_ms,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `service_probe_${target || "scan"}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Cloud icon component with fallback
function CloudIcon({ cloudInfo }: { cloudInfo: { provider: string; icon: string } | null }) {
  const [hasError, setHasError] = React.useState(false);

  if (!cloudInfo || hasError) {
    return <Server className="h-5 w-5 text-muted-foreground" />;
  }

  return (
    <img
      src={cloudInfo.icon}
      alt={cloudInfo.provider}
      className="h-6 w-6"
      onError={() => setHasError(true)}
    />
  );
}

// Target Card - shows IP with subdomain and cloud info
function TargetCard({ result }: { result: ServiceProbeResultData }) {
  const [isOpen, setIsOpen] = React.useState(true);
  const cloudInfo = detectCloudProvider(result.probes || []);
  const probes = result.probes || [];
  const identifiedCount = probes.filter(p => p.service_name).length;

  return (
    <div className="rounded-lg border border-border bg-card/50 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          {/* Cloud/Server Icon */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <CloudIcon cloudInfo={cloudInfo} />
          </div>

          {/* Target Info */}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono font-semibold">{result.target}</code>
              {cloudInfo && (
                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                  <Cloud className="h-3 w-3 mr-1" />
                  {cloudInfo.provider}
                </Badge>
              )}
            </div>
            {/* Show all subdomains that resolve to this IP */}
            {(result.source_subdomains?.length || result.source_subdomain) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 flex-wrap">
                <Globe className="h-3 w-3 flex-shrink-0" />
                {result.source_subdomains?.length ? (
                  <span className="flex flex-wrap gap-1">
                    {result.source_subdomains.map((subdomain, idx) => (
                      <span key={subdomain}>
                        <span className="font-mono">{subdomain}</span>
                        {idx < result.source_subdomains!.length - 1 && (
                          <span className="text-border mx-1">•</span>
                        )}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="font-mono">{result.source_subdomain}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats & Toggle */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-sm">
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">{probes.length}</span> ports
            </span>
            <span className="text-muted-foreground">
              <span className="font-semibold text-emerald-400">{identifiedCount}</span> identified
            </span>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-border p-4 space-y-2">
          {probes.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No probes for this target</p>
          ) : (
            <div className="grid gap-2">
              {probes.map((probe, idx) => (
                <div
                  key={`${probe.port}-${idx}`}
                  className="flex items-start justify-between p-3 rounded-lg bg-muted/30 border border-border/50"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded bg-background border border-border">
                      <Network className="h-4 w-4 text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono font-semibold">
                          {probe.port}/{probe.protocol}
                        </code>
                        {probe.service_name && (
                          <Badge variant="secondary" className="text-xs">
                            {formatServiceName(probe.service_name)}
                          </Badge>
                        )}
                        {probe.service_product && (
                          <span className="text-xs text-muted-foreground">
                            {probe.service_product}
                            {probe.service_version && ` ${probe.service_version}`}
                          </span>
                        )}
                      </div>
                      {probe.http_info?.server && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Server: <span className="text-foreground">{probe.http_info.server}</span>
                        </div>
                      )}
                      {probe.http_info?.title && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Title: <span className="text-foreground">{probe.http_info.title}</span>
                        </div>
                      )}
                      {probe.raw_banner && !probe.http_info && (
                        <div className="text-xs text-muted-foreground mt-1 max-w-md truncate">
                          Banner: <span className="font-mono text-foreground">{probe.raw_banner.slice(0, 80)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {METHOD_LABELS[probe.probe_method] || probe.probe_method}
                    </Badge>
                    <span className={cn("text-xs font-medium", getConfidenceColor(probe.confidence))}>
                      {probe.confidence}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ServiceProbeModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: ServiceProbeModuleViewProps) {
  // Guard against null/undefined data
  if (!data) {
    return (
      <ModuleContainer
        moduleName="service_probe"
        icon={Fingerprint}
        data={{}}
        isLoading={isLoading}
        error={error || "No data available"}
        onRetry={onRetry}
        className={className}
        hideHeader={hideHeader}
      >
        <div className="text-center py-8 text-muted-foreground">
          No service probe data available
        </div>
      </ModuleContainer>
    );
  }

  // Collect all probes from multi-target results or single target
  const allProbes = React.useMemo(() => {
    try {
      if (data.results && Array.isArray(data.results) && data.results.length > 0) {
        return data.results.flatMap((r) => r?.probes || []).filter(Boolean);
      }
      return Array.isArray(data.probes) ? data.probes : [];
    } catch {
      return [];
    }
  }, [data]);

  const identifiedProbes = allProbes.filter((p) => p && p.service_name);
  const versionedProbes = allProbes.filter((p) => p && p.service_version);

  const aggregateStats = data.aggregate_stats || {
    total_ports_probed: allProbes.length,
    total_identified: identifiedProbes.length,
    all_unique_services: [...new Set(identifiedProbes.map((p) => p.service_name).filter(Boolean) as string[])],
    all_unique_products: [...new Set(versionedProbes.filter((p) => p.service_product).map((p) => p.service_product).filter(Boolean) as string[])],
    total_duration_ms: Array.isArray(data.results) ? data.results.reduce((acc, r) => acc + (r?.probe_duration_ms || 0), 0) : 0,
  };

  // Probe columns
  const columns: ColumnDef<ProbeResult>[] = [
    {
      id: "port",
      header: "Port",
      accessor: (row) => row.port,
      cell: (value) => (
        <code className="text-sm font-mono font-semibold">{value as number}</code>
      ),
      sortable: true,
      width: "80px",
    },
    {
      id: "service",
      header: "Service",
      accessor: (row) => row.service_name || "",
      cell: (_, row) => {
        const IconComponent = row.service_name ? SERVICE_ICONS[row.service_name] || Wifi : Wifi;
        return (
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <IconComponent className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">
                {row.service_name ? formatServiceName(row.service_name) : <span className="text-muted-foreground">Unknown</span>}
              </span>
              {row.service_product && (
                <span className="text-xs text-muted-foreground">
                  ({row.service_product}
                  {row.service_version && ` ${row.service_version}`})
                </span>
              )}
            </div>
            {row.os_hint && (
              <div className="text-xs text-muted-foreground mt-1">OS: {row.os_hint}</div>
            )}
            {/* Show banner for non-HTTP probes */}
            {!row.http_info && (
              <BannerDisplay banner={row.raw_banner} hex={row.banner_hex} />
            )}
            {/* Show HTTP info for HTTP probes */}
            {row.http_info && <HttpInfoDisplay info={row.http_info} />}
          </div>
        );
      },
      sortable: true,
      filterable: true,
    },
    {
      id: "method",
      header: "Method",
      accessor: (row) => row.probe_method,
      cell: (value) => (
        <Badge variant="outline" className="text-xs">
          {METHOD_LABELS[value as ProbeMethod] || String(value)}
        </Badge>
      ),
      sortable: true,
      width: "140px",
    },
    {
      id: "confidence",
      header: "Confidence",
      accessor: (row) => row.confidence,
      cell: (value) => (
        <span className={cn("text-sm font-medium", getConfidenceColor(value as number))}>
          {value as number}%
        </span>
      ),
      sortable: true,
      align: "right",
      width: "100px",
    },
    {
      id: "probe_time_ms",
      header: "Response",
      accessor: (row) => row.probe_time_ms,
      cell: (value) => (
        <span className="text-xs font-mono text-muted-foreground">{value as number}ms</span>
      ),
      sortable: true,
      align: "right",
      width: "100px",
    },
  ];

  return (
    <ModuleContainer
      moduleName="service_probe"
      icon={Fingerprint}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Standalone Mode Notice */}
        {data.standalone_mode && (
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <div className="flex items-center gap-2 text-sm text-blue-400">
              <Fingerprint className="h-4 w-4" />
              <span>Running in standalone mode - probing common service ports</span>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="flex items-start justify-between gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Server className="h-3 w-3 text-cyan-400" /> Ports Probed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-cyan-400">
                  {aggregateStats.total_ports_probed}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Fingerprint className="h-3 w-3 text-emerald-400" /> Identified
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-emerald-400">
                  {aggregateStats.total_identified}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Code className="h-3 w-3 text-purple-400" /> Versioned
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-purple-400">
                  {versionedProbes.length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Duration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold">{formatDuration(aggregateStats.total_duration_ms)}</p>
              </CardContent>
            </Card>
          </div>
          {/* Export Button */}
          {allProbes.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => exportProbesCsv(allProbes, data.source_domain)}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          )}
        </div>

        {/* Target Info */}
        {(data.results && data.results.length > 0) ? (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Source Domain</p>
                  <code className="text-sm font-mono">{data.source_domain}</code>
                </div>
              </div>
              <div className="border-l border-border pl-4">
                <p className="text-xs text-muted-foreground">Targets Probed</p>
                <p className="text-sm font-semibold">{data.probed_targets} / {data.total_requested}</p>
              </div>
            </div>
          </div>
        ) : data.target && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center gap-4">
              <Server className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Target</p>
                <code className="text-sm font-mono">{data.target}</code>
              </div>
            </div>
          </div>
        )}

        {/* Unique Services */}
        {aggregateStats.all_unique_services.length > 0 && (
          <ModuleSection
            title="Detected Services"
            icon={Wifi}
            badge={<Badge variant="outline" className="text-xs">{aggregateStats.all_unique_services.length}</Badge>}
          >
            <div className="flex flex-wrap gap-2.5">
              {aggregateStats.all_unique_services.map((service) => {
                const IconComponent = SERVICE_ICONS[service.toLowerCase()] || Wifi;
                const category = getServiceCategory(service);
                const styles = CATEGORY_STYLES[category];
                return (
                  <div
                    key={service}
                    className={cn(
                      "inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border font-medium text-sm",
                      "transition-all duration-200 hover:scale-105",
                      styles.bg,
                      styles.border,
                      styles.text,
                      styles.glow
                    )}
                  >
                    <IconComponent className="h-4 w-4" />
                    <span className="font-semibold tracking-wide">{formatServiceName(service)}</span>
                  </div>
                );
              })}
            </div>
          </ModuleSection>
        )}

        {/* Unique Products */}
        {aggregateStats.all_unique_products.length > 0 && (
          <ModuleSection
            title="Detected Products"
            icon={Code}
            badge={<Badge variant="outline" className="text-xs">{aggregateStats.all_unique_products.length}</Badge>}
          >
            <div className="flex flex-wrap gap-2.5">
              {aggregateStats.all_unique_products.map((product) => {
                const style = getProductStyle(product);
                return (
                  <div
                    key={product}
                    className={cn(
                      "inline-flex items-center px-3.5 py-2 rounded-lg border font-medium text-sm",
                      "transition-all duration-200 hover:scale-105",
                      style.bg,
                      style.border,
                      style.text
                    )}
                  >
                    <span className="font-semibold">{product}</span>
                  </div>
                );
              })}
            </div>
          </ModuleSection>
        )}

        {/* Probed Targets - Grouped by IP with subdomain and cloud info */}
        {data.results && Array.isArray(data.results) && data.results.length > 0 && (
          <ModuleSection
            title="Probed Targets"
            icon={Server}
            badge={<Badge variant="outline" className="text-xs">{data.results.length} targets</Badge>}
          >
            <div className="space-y-3">
              {data.results.map((result, idx) => (
                <TargetCard key={result.target || idx} result={result} />
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Service Distribution Chart */}
        {identifiedProbes.length > 1 && (
          <ModuleSection
            title="Service Distribution"
            icon={Server}
          >
            <ServiceDistributionChart probes={identifiedProbes} />
          </ModuleSection>
        )}

        {/* Probes Table - Only show if not using grouped view */}
        {allProbes.length > 0 && (!data.results || data.results.length === 0) && (
          <ModuleSection
            title="Probe Results"
            icon={Fingerprint}
            badge={<Badge variant="outline" className="text-xs">{allProbes.length}</Badge>}
          >
            <ModuleDataTable
              data={allProbes}
              columns={columns}
              paginated={allProbes.length > 15}
              searchable
              searchPlaceholder="Search ports or services..."
            />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

ServiceProbeModuleView.displayName = "ServiceProbeModuleView";
